import React, { useCallback, useEffect, useState } from 'react';
import { ShieldAlert, RefreshCw, CheckCircle2, Lock } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { supabase } from '@/lib/supabase';
import { money } from '@/data/constants';

/**
 * REFUND / CHARGEBACK PROTECTION (admin view)
 * ---------------------------------------------------------------------------
 * Credits are only valid while the payment that bought them is still valid.
 * When payment-webhook receives a refund, dispute, chargeback or reversal it
 * calls mkt_apply_payment_reversal, which performs SIX actions in ONE
 * transaction so no half-state can exist:
 *
 *   1. marks the charge refunded / disputed in mkt_payments
 *   2. suspends the membership (mkt_subscriptions -> suspended)
 *   3. freezes every remaining credit (plan + one-off) to zero, with a
 *      negative mkt_credit_ledger row for the audit trail
 *   4. blocks new unlocks (mkt_profiles.status -> suspended; the claim
 *      function already refuses any profile that is not 'approved')
 *   5. raises a critical admin alert
 *   6. writes mkt_security_events + mkt_activity + a case row here
 *
 * Reinstating deliberately does NOT return credits — a new verified payment
 * is what restores a balance. Anything else would re-open the "spend them,
 * then take the money back" hole this exists to close.
 */

interface Case {
  id: string;
  user_id: string | null;
  email: string | null;
  amount: number;
  reason: string | null;
  status: string;
  admin_note: string | null;
  external_payment_id: string | null;
  created_at: string;
  resolved_at: string | null;
}

const tone = (s: string) =>
  s === 'disputed'
    ? 'bg-red-50 text-red-700 ring-red-200'
    : s === 'refunded'
    ? 'bg-amber-50 text-amber-800 ring-amber-200'
    : 'bg-emerald-50 text-emerald-700 ring-emerald-200';

const AdminDisputes: React.FC = () => {
  const { lang } = useI18n();
  const es = lang === 'es';
  const [cases, setCases] = useState<Case[]>([]);
  const [suspended, setSuspended] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string>('');

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: rows }, { count }] = await Promise.all([
      supabase
        .from('mkt_refunds')
        .select('id,user_id,email,amount,reason,status,admin_note,external_payment_id,created_at,resolved_at')
        .order('created_at', { ascending: false })
        .limit(40),
      supabase
        .from('mkt_profiles')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'suspended'),
    ]);
    setCases((rows as Case[]) || []);
    setSuspended(typeof count === 'number' ? count : null);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const reinstate = async (c: Case) => {
    if (!c.user_id) return;
    setBusy(c.id);
    setMsg('');
    const { data, error } = await supabase.rpc('mkt_reinstate_account', {
      p_user: c.user_id,
      p_note: `Reinstated after reviewing case ${c.id.slice(0, 8)}`,
    });
    setBusy(null);
    if (error || (data && (data as any).ok === false)) {
      setMsg(
        es
          ? `No se pudo reactivar: ${error?.message || (data as any)?.reason}`
          : `Could not reinstate: ${error?.message || (data as any)?.reason}`
      );
      return;
    }
    setMsg(
      es
        ? 'Cuenta reactivada. Los créditos NO se devolvieron — hace falta un pago verificado.'
        : 'Account reinstated. Credits were NOT returned — a verified payment is required.'
    );
    load();
  };

  const open = cases.filter((c) => c.status === 'refunded' || c.status === 'disputed');

  return (
    <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-extrabold text-slate-900">
            <ShieldAlert className="h-5 w-5 text-red-500" />
            {es ? 'Reembolsos y contracargos' : 'Refunds & chargebacks'}
          </h2>
          <p className="mt-1 max-w-3xl text-sm text-slate-600">
            {es
              ? 'Dinero devuelto = derecho revocado. Un reembolso o disputa congela los créditos restantes, suspende la membresía y bloquea nuevos desbloqueos automáticamente.'
              : 'Money reversed = entitlement revoked. A refund or dispute automatically freezes remaining credits, suspends the membership and blocks new unlocks.'}
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          {es ? 'Actualizar' : 'Refresh'}
        </button>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl bg-slate-50 p-4">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
            {es ? 'Casos abiertos' : 'Open cases'}
          </p>
          <p className="mt-1 text-2xl font-extrabold text-slate-900">{open.length}</p>
        </div>
        <div className="rounded-xl bg-slate-50 p-4">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
            {es ? 'Cuentas suspendidas' : 'Suspended accounts'}
          </p>
          <p className="mt-1 text-2xl font-extrabold text-slate-900">
            {suspended === null ? '—' : suspended}
          </p>
        </div>
        <div className="rounded-xl bg-emerald-50 p-4">
          <p className="text-xs font-bold uppercase tracking-wide text-emerald-700">
            {es ? 'Protección' : 'Protection'}
          </p>
          <p className="mt-1 flex items-center gap-1.5 text-sm font-extrabold text-emerald-800">
            <Lock className="h-4 w-4" />
            {es ? 'Automática y auditada' : 'Automatic & audited'}
          </p>
        </div>
      </div>

      {msg && (
        <p className="mt-4 rounded-xl bg-slate-900 px-4 py-3 text-sm font-bold text-white">{msg}</p>
      )}

      <div className="mt-5 overflow-x-auto">
        <table className="w-full min-w-[820px] text-left text-sm">
          <thead className="border-b border-slate-200 text-xs font-bold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="py-2 pr-3">{es ? 'Cuenta' : 'Account'}</th>
              <th className="py-2 pr-3">{es ? 'Monto' : 'Amount'}</th>
              <th className="py-2 pr-3">{es ? 'Tipo' : 'Type'}</th>
              <th className="py-2 pr-3">{es ? 'Motivo' : 'Reason'}</th>
              <th className="py-2 pr-3">{es ? 'Transacción' : 'Transaction'}</th>
              <th className="py-2 pr-3">{es ? 'Acción' : 'Action'}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {cases.length === 0 && !loading && (
              <tr>
                <td colSpan={6} className="py-6 text-center text-sm text-slate-500">
                  {es
                    ? 'Sin reembolsos ni disputas registradas. La protección está activa y esperando.'
                    : 'No refunds or disputes recorded. Protection is armed and waiting.'}
                </td>
              </tr>
            )}
            {cases.map((c) => (
              <tr key={c.id}>
                <td className="py-3 pr-3">
                  <p className="font-bold text-slate-900">{c.email || '—'}</p>
                  <p className="text-xs text-slate-400">{new Date(c.created_at).toLocaleString()}</p>
                </td>
                <td className="py-3 pr-3 font-extrabold text-slate-900">{money(c.amount || 0)}</td>
                <td className="py-3 pr-3">
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold uppercase ring-1 ${tone(c.status)}`}>
                    {c.status}
                  </span>
                </td>
                <td className="py-3 pr-3 text-slate-600">
                  <p>{c.reason || '—'}</p>
                  {c.admin_note && <p className="text-xs text-slate-400">{c.admin_note}</p>}
                </td>
                <td className="py-3 pr-3">
                  <code className="break-all font-mono text-[11px] text-slate-500">
                    {c.external_payment_id || '—'}
                  </code>
                </td>
                <td className="py-3 pr-3">
                  {c.status === 'resolved' ? (
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700">
                      <CheckCircle2 className="h-4 w-4" />
                      {es ? 'Resuelto' : 'Resolved'}
                    </span>
                  ) : (
                    <button
                      onClick={() => reinstate(c)}
                      disabled={busy === c.id || !c.user_id}
                      className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-bold text-white hover:bg-slate-800 disabled:opacity-50"
                    >
                      {busy === c.id
                        ? es
                          ? 'Aplicando…'
                          : 'Applying…'
                        : es
                        ? 'Levantar suspensión'
                        : 'Lift suspension'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600">
        <p className="font-bold uppercase tracking-wide text-slate-500">
          {es ? 'Qué ocurre automáticamente' : 'What happens automatically'}
        </p>
        <ol className="mt-2 list-decimal space-y-1 pl-5">
          <li>{es ? 'El cargo se marca reembolsado/disputado' : 'The charge is marked refunded / disputed'}</li>
          <li>{es ? 'La membresía queda suspendida' : 'The membership is suspended'}</li>
          <li>{es ? 'Los créditos restantes se congelan a cero' : 'Remaining credits are frozen to zero'}</li>
          <li>{es ? 'Se bloquean nuevos desbloqueos' : 'New lead unlocks are blocked'}</li>
          <li>{es ? 'Se genera una alerta crítica de admin' : 'A critical admin alert is raised'}</li>
          <li>{es ? 'Se registra en el log de auditoría' : 'It is written to the audit log'}</li>
        </ol>
        <p className="mt-3">
          {es
            ? 'Levantar la suspensión reactiva el acceso pero NO devuelve créditos: sólo un pago verificado nuevo restablece el saldo.'
            : 'Lifting a suspension restores access but does NOT return credits: only a new verified payment restores a balance.'}
        </p>
      </div>
    </section>
  );
};

export default AdminDisputes;
